"use client";
import { Link } from "@/i18n/routing";
import { getProfile } from "@/lib/user";
import CloseIcon from "@mui/icons-material/Close";
import MenuIcon from "@mui/icons-material/Menu";
import { AppBar, Avatar, Box, Button, Container, IconButton, Menu, MenuItem, Typography } from "@mui/material";
import Drawer from "@mui/material/Drawer";
import { signOut } from "next-auth/react";
import { useTranslations } from "next-intl";
import { useRouter, usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import LanguageSwitch from "./LanguageSwitch";
import Logo from "./Logo";
import ThemeToggle from "./ThemeToggle";
import { useIsLoggedIn } from "@/lib/hooks/useIsLoggedIn";
import FreeTrialBadge from "./FreeTrialBadge";
import NewUserGuide from "./NewUserGuide";

export default function Navbar() {
  const t = useTranslations("Navbar");
  const isLoggedIn = useIsLoggedIn();
  const router = useRouter();
  const pathname = usePathname();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [username, setUsername] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));

  useEffect(() => {
    async function loadProfile() {
      if (isLoggedIn) {
        try {
          const profile = await getProfile();
          if (!profile) {
            signOut({ callbackUrl: pathname });
          }
          setUsername(profile?.name || null);
        } catch (error) {
          console.error("Failed to load profile:", error);
        }
      }
    }
    loadProfile();
  }, [isLoggedIn]);

  const getInitials = (name: string) => {
    return name.charAt(0).toUpperCase();
  };

  const productsMenuItems = [{ label: t("productsMenu.analytics"), href: "/products/analytics" }];

  const handleAvatarClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleProfileClick = () => {
    router.push("/profile");
    handleMenuClose();
  };

  const handleSignOutClick = () => {
    signOut({ callbackUrl: "/" });
    handleMenuClose();
  };

  const handleDrawerOpen = () => setDrawerOpen(true);
  const handleDrawerClose = () => setDrawerOpen(false);

  // 如果视口变为 md 或更大，确保移动端 Drawer 被关闭。
  // Drawer 组件使用响应式 `display: { xs: 'block', md: 'none' }`，
  // 因此当视口变为 md+ 时 Drawer 会被 CSS 隐藏；关闭 Drawer 可保持 React 状态同步，避免视觉/状态不一致。
  useEffect(() => {
    if (isMdUp && drawerOpen) setDrawerOpen(false);
  }, [isMdUp, drawerOpen]);

  return (
    <>
      <AppBar
        id="navbar"
        position="fixed"
        elevation={0}
        sx={{
          boxShadow: "none",
          backgroundColor: "background.default",
          height: "5.5rem",
          display: "flex",
          justifyContent: "center",
        }}>
        <Container maxWidth={false}>
          <Box sx={{ display: "flex", alignItems: "center", py: 4 }}>
            {/* Logo - Left Section */}
            <Box sx={{ flexShrink: 1, minWidth: 0, display: "flex", alignItems: "baseline" }}>
              <Link href="/" style={{ display: "flex", alignItems: "baseline", gap: 2, minWidth: 0 }}>
                <Logo />
                <Typography
                  variant="h6"
                  color="#BC844B"
                  sx={{
                    fontFamily: "var(--font-montserrat)",
                    lineHeight: 1,
                    fontWeight: 600,
                    textWrap: "nowrap",
                    minWidth: 0,
                  }}>
                  Right Pulse
                </Typography>
              </Link>
            </Box>

            <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "0.5rem" }}>
              {/* Navigation Links */}
              <Box sx={{ display: { xs: "none", md: "flex" }, alignItems: "center", gap: { xl: "0.5rem" } }}>
                <Link href="/research" className="user-guide-target-desktop">
                  <Button
                    variant="text"
                    sx={{
                      fontSize: "var(--font-size-xs)",
                      fontWeight: 600,
                      color: "text.primary",
                      textTransform: "none",
                      px: { xs: "0.5rem", lg: "1rem" },
                      "&:hover": { color: "primary.main" },
                    }}>
                    {t("research")}
                  </Button>
                </Link>
                <Link href="/products/analytics" className="user-guide-target-desktop">
                  <Button
                    variant="text"
                    sx={{
                      fontSize: "var(--font-size-xs)",
                      fontWeight: 600,
                      color: "text.primary",
                      textTransform: "none",
                      textWrap: "nowrap",
                      px: { xs: "0.5rem", lg: "1rem" },
                      "&:hover": { color: "primary.main" },
                    }}>
                    {t("signals")}
                  </Button>
                </Link>
                <Link href="/products/citadel" className="user-guide-target-desktop">
                  <Button
                    variant="text"
                    sx={{
                      fontSize: "var(--font-size-xs)",
                      fontWeight: 600,
                      color: "text.primary",
                      textTransform: "none",
                      textWrap: "nowrap",
                      px: { xs: "0.5rem", lg: "1rem" },
                      "&:hover": { color: "primary.main" },
                    }}>
                    {t("citadel")}
                  </Button>
                </Link>
                <Link href="/products/miningHub" className="user-guide-target-desktop">
                  <Button
                    variant="text"
                    sx={{
                      fontSize: "var(--font-size-xs)",
                      fontWeight: 600,
                      color: "text.primary",
                      textTransform: "none",
                      textWrap: "nowrap",
                      px: { xs: "0.5rem", lg: "1rem" },
                      "&:hover": { color: "primary.main" },
                    }}>
                    {t("miningHub")}
                  </Button>
                </Link>
                <Link href="/membership">
                  <Button
                    variant="text"
                    sx={{
                      fontSize: "var(--font-size-xs)",
                      fontWeight: 600,
                      color: "text.primary",
                      textTransform: "none",
                      px: { xs: "0.5rem", lg: "1rem" },
                      "&:hover": { color: "primary.main" },
                    }}>
                    {t("membership")}
                  </Button>
                </Link>
              </Box>
              <Box
                sx={{
                  display: { xs: drawerOpen ? "none" : "flex", md: "flex" },
                  alignItems: "center",
                }}>
                {/* 仅在 xs sm 屏幕且 Drawer 打开时隐藏右侧组件，其它情况始终显示 */}
                {/* Auth Buttons */}
                {isLoggedIn ? (
                  <>
                    <IconButton onClick={handleAvatarClick} sx={{ p: 0 }}>
                      <Avatar
                        sx={{
                          bgcolor: "primary.main",
                          width: "2.375rem",
                          height: "2.375rem",
                          fontSize: "var(--font-size-xs)",
                          fontWeight: 600,
                        }}>
                        {getInitials(username || "")}
                      </Avatar>
                    </IconButton>
                    <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                      <MenuItem onClick={handleProfileClick}>{t("profile")}</MenuItem>
                      <MenuItem onClick={handleSignOutClick}>{t("signOut")}</MenuItem>
                    </Menu>
                  </>
                ) : (
                  <>
                    <Box sx={{ display: { xs: "none", lg: "flex" }, gap: 1 }}>
                      <Link href="/auth/signin">
                        <Button
                          variant="contained"
                          color="secondary"
                          sx={{
                            minWidth: "7.5rem",
                            fontSize: "var(--font-size-xs)",
                            lineHeight: 1.6,
                            whiteSpace: "nowrap",
                            textWrap: "none",
                            bgcolor: "secondary.main",
                            px: 3,
                            py: 1,
                            "&:hover": {
                              bgcolor: "secondary.dark",
                            },
                          }}>
                          {t("signIn")}
                        </Button>
                      </Link>
                      <Link href="/auth/signup">
                        <Box sx={{ position: "relative", display: "inline-block" }}>
                          <Button
                            variant="contained"
                            sx={{
                              minWidth: "7.5rem",
                              fontSize: "var(--font-size-xs)",
                              lineHeight: 1.6,
                              whiteSpace: "nowrap",
                              textWrap: "none",
                              bgcolor: "primary.main",
                              px: 3,
                              py: 1,
                              "&:hover": {
                                bgcolor: "primary.dark",
                              },
                            }}>
                            {t("signUp")}
                          </Button>
                          <FreeTrialBadge />
                        </Box>
                      </Link>
                    </Box>
                  </>
                )}

                {/* Theme and Language Controls */}
                <Box sx={{ display: { xs: "none", md: "flex" }, alignItems: "center", gap: 1.5, ml: "1rem" }}>
                  <LanguageSwitch />
                  <ThemeToggle />
                </Box>
                {/* md以下显示菜单展开icon */}
                <Box sx={{ display: { xs: "flex", md: "none" } }}>
                  <IconButton onClick={handleDrawerOpen} size="small">
                    <MenuIcon sx={{ fontSize: 28 }} />
                  </IconButton>
                </Box>
              </Box>
            </Box>
          </Box>
        </Container>
        {/* Drawer for mobile menu */}
        <Drawer
          anchor="top"
          open={drawerOpen}
          onClose={handleDrawerClose}
          hideBackdrop
          ModalProps={{
            onClose: handleDrawerClose,
          }}
          sx={{
            display: { xs: "block", md: "none" },
            zIndex: (theme) => theme.zIndex.appBar + 1,
            "& .MuiDrawer-paper": {
              width: "100vw",
              height: "100vh",
              top: 0,
              p: 0,
              m: 0,
              backgroundColor: "background.default",
              boxShadow: "none",
              borderRadius: 0,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            },
          }}
          PaperProps={{
            sx: {
              pt: 0,
              px: 0,
              borderRadius: 0,
              boxShadow: "none",
              backgroundColor: "background.default",
              minHeight: "100vh",
              display: "flex",
              flexDirection: "column",
            },
          }}>
          {/* Drawer 顶部自定义导航栏 */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              height: "5.5rem",
              px: 3,
              flexShrink: 0,
              backgroundColor: "background.default",
            }}>
            <Link href="/" className="flex items-baseline gap-2" onClick={handleDrawerClose}>
              <Logo />
              <Typography
                variant="h6"
                color="#BC844B"
                sx={{ fontFamily: "var(--font-montserrat)", lineHeight: 1, fontWeight: 600 }}>
                Right Pulse
              </Typography>
            </Link>
            <IconButton onClick={handleDrawerClose} size="small">
              <CloseIcon sx={{ fontSize: 28 }} />
            </IconButton>
          </Box>
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              height: "100%",
              px: 0,
              backgroundColor: "background.default",
            }}>
            {/* 菜单内容 */}
            <Box sx={{ px: 3, pt: 4, pb: 0 }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                <Link
                  href="/research"
                  onClick={handleDrawerClose}
                  className="user-guide-target-mobile"
                  style={{ textDecoration: "none" }}>
                  <Typography sx={{ color: "text.primary", fontWeight: 600, fontSize: "1.15rem", py: 1 }}>
                    {t("research")}
                  </Typography>
                </Link>
                <Link
                  href="/products/analytics"
                  onClick={handleDrawerClose}
                  className="user-guide-target-mobile"
                  style={{ textDecoration: "none" }}>
                  <Typography sx={{ color: "text.primary", fontWeight: 600, fontSize: "1.15rem", py: 1 }}>
                    {t("signals")}
                  </Typography>
                </Link>
                <Link
                  href="/products/citadel"
                  onClick={handleDrawerClose}
                  className="user-guide-target-mobile"
                  style={{ textDecoration: "none" }}>
                  <Typography sx={{ color: "text.primary", fontWeight: 600, fontSize: "1.15rem", py: 1 }}>
                    {t("citadel")}
                  </Typography>
                </Link>
                <Link
                  href="/products/miningHub"
                  onClick={handleDrawerClose}
                  className="user-guide-target-mobile"
                  style={{ textDecoration: "none" }}>
                  <Typography sx={{ color: "text.primary", fontWeight: 600, fontSize: "1.15rem", py: 1 }}>
                    {t("miningHub")}
                  </Typography>
                </Link>
                <Link href="/membership" onClick={handleDrawerClose} style={{ textDecoration: "none" }}>
                  <Typography sx={{ color: "text.primary", fontWeight: 600, fontSize: "1.15rem", py: 1 }}>
                    {t("membership")}
                  </Typography>
                </Link>
              </Box>
              <Box sx={{ borderBottom: 1, borderColor: "divider", my: 4 }} />
              {/* 语言和主题 */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Typography sx={{ color: "text.secondary", fontWeight: 500, fontSize: "1rem" }}>
                    {t("language")}
                  </Typography>
                  <LanguageSwitch />
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Typography sx={{ color: "text.secondary", fontWeight: 500, fontSize: "1rem" }}>
                    {t("theme")}
                  </Typography>
                  <ThemeToggle />
                </Box>
              </Box>
            </Box>
            {/* 底部按钮 */}
            {!isLoggedIn && (
              <Box
                sx={{
                  mt: "auto",
                  px: 3,
                  pb: 4,
                  pt: 6,
                  display: "flex",
                  gap: 3,
                  width: "100%",
                }}>
                <Link href="/auth/signin" onClick={handleDrawerClose} style={{ flex: 1 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="secondary"
                    sx={{
                      fontWeight: 600,
                      fontSize: "1.1rem",
                      borderRadius: "2rem",
                      py: 1.5,
                      px: 0,
                    }}>
                    {t("signIn")}
                  </Button>
                </Link>
                <Link href="/auth/signup" onClick={handleDrawerClose} style={{ flex: 1 }}>
                  <Box sx={{ position: "relative" }}>
                    <Button
                      fullWidth
                      variant="contained"
                      color="primary"
                      sx={{
                        fontWeight: 600,
                        fontSize: "1.1rem",
                        borderRadius: "2rem",
                        py: 1.5,
                        px: 0,
                      }}>
                      {t("signUp")}
                    </Button>
                    <FreeTrialBadge />
                  </Box>
                </Link>
              </Box>
            )}
          </Box>
        </Drawer>
      </AppBar>
      {isLoggedIn !== undefined && <NewUserGuide drawerOpen={drawerOpen} />}
    </>
  );
}
